/**
 * PDP gallery inline video autoplay.
 *
 * The product gallery renders videos inside <deferred-media> with the real
 * <video>/<iframe> kept in a <template> (so it isn't loaded until activated),
 * and the poster routes clicks to the fullscreen modal. As a result the inline
 * gallery video never autoplays on its own.
 *
 * Triggers play when a video slide becomes active:
 *   - `slideChanged` from the slider component (mobile swipe / arrows) — the
 *     reliable signal for the horizontal mobile slider.
 *   - IntersectionObserver (desktop stacked scroll, thumbnail nav, initial load).
 *
 * Mobile browsers only allow programmatic autoplay when the video is muted AND
 * playsinline, so both are forced (property + attribute) before every play.
 */
(function () {
  'use strict';

  function primeVideo(video) {
    // Required for autoplay on iOS / Android — set as property AND attribute.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  function loadDeferred(deferred) {
    if (deferred.getAttribute('loaded')) {
      return deferred.querySelector('video, iframe');
    }
    var template = deferred.querySelector('template');
    if (!template || !template.content || !template.content.firstElementChild) return null;

    var wrap = document.createElement('div');
    wrap.appendChild(template.content.firstElementChild.cloneNode(true));
    var media = wrap.querySelector('video, iframe');
    if (!media) return null;

    if (media.nodeName === 'VIDEO') {
      primeVideo(media);
      media.preload = 'auto';
    }

    deferred.setAttribute('loaded', 'true'); // also hides the poster via CSS
    deferred.appendChild(media);
    return media;
  }

  function playVideoEl(video) {
    primeVideo(video); // re-assert in case the element was loaded by another path
    var attempt = function () {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
    };
    if (video.readyState >= 2) {
      attempt();
    } else {
      // preload="none" videos have no data yet — wait until they can play.
      video.addEventListener('canplay', attempt, { once: true });
      try { video.load(); } catch (e) {}
      attempt(); // also try immediately; harmless if it no-ops
    }
  }

  function play(deferred) {
    var media = loadDeferred(deferred);
    if (!media) return;

    if (media.nodeName === 'VIDEO') {
      playVideoEl(media);
    } else {
      // iframe (YouTube/Vimeo) — give the embed a tick to expose contentWindow.
      setTimeout(function () {
        if (media.classList.contains('js-youtube') && media.contentWindow) {
          try {
            media.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } catch (e) {}
        } else if (media.classList.contains('js-vimeo') && media.contentWindow) {
          try {
            media.contentWindow.postMessage('{"method":"play"}', '*');
          } catch (e) {}
        }
      }, 60);
    }
  }

  function pause(deferred) {
    var v = deferred.querySelector('video');
    if (v) {
      try { v.pause(); } catch (e) {}
    }
    var yt = deferred.querySelector('iframe.js-youtube');
    if (yt && yt.contentWindow) {
      try { yt.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); } catch (e) {}
    }
    var vm = deferred.querySelector('iframe.js-vimeo');
    if (vm && vm.contentWindow) {
      try { vm.contentWindow.postMessage('{"method":"pause"}', '*'); } catch (e) {}
    }
  }

  function isVideoDeferred(deferred) {
    if (deferred.closest('.custom-gallery-modal')) return false; // modal handles itself
    var tpl = deferred.querySelector('template');
    return !!(tpl && tpl.content && tpl.content.querySelector('video, iframe'));
  }

  function collectTargets(gallery) {
    var targets = [];
    gallery.querySelectorAll('deferred-media').forEach(function (d) {
      if (isVideoDeferred(d)) targets.push(d);
    });
    return targets;
  }

  function init(gallery) {
    if (gallery.dataset.autoplayBound === 'true') return;

    var targets = collectTargets(gallery);
    if (!targets.length) return;

    gallery.dataset.autoplayBound = 'true';

    // 1) slideChanged — fires on mobile swipe / arrow nav with the active slide.
    var viewer = gallery.querySelector('[id^="GalleryViewer"]') || gallery;
    viewer.addEventListener('slideChanged', function (event) {
      var current = event && event.detail ? event.detail.currentElement : null;
      targets.forEach(function (d) {
        if (current && current.contains(d)) {
          play(d);
        } else {
          pause(d);
        }
      });
    });

    // 2) IntersectionObserver — desktop scroll, thumbnail nav, initial active slide.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            play(entry.target);
          } else if (entry.intersectionRatio <= 0.2) {
            pause(entry.target);
          }
        });
      }, { threshold: [0, 0.2, 0.5, 0.75, 1] });

      targets.forEach(function (d) { io.observe(d); });
    }
  }

  function boot() {
    document.querySelectorAll('media-gallery, .product__media-gallery').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
